import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag, Truck, ChevronRight, ArrowLeft, Shield } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import {
  getCart,
  updateCartQty,
  removeFromCart,
  clearCart,
  getToken,
} from '../api/auth';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


const BADGE_COLORS = {
  'New':        { bg: '#1C1A18', color: '#FAF8F4' },
  'Bestseller': { bg: '#4A3728', color: '#D4AA70' },
  'Sale':       { bg: '#C0392B', color: '#fff' },
  'Limited':    { bg: '#2C2C2C', color: '#D4AF78' },
  'Trending':   { bg: '#B08D57', color: '#fff' },
  'Hot':        { bg: '#C0392B', color: '#fff' },
  'Exclusive':  { bg: '#1C1A18', color: '#D4AF78' },
};

const CartPage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const [items, setItems]                     = useState([]);
  const [cartLoading, setCartLoading]         = useState(true);

  const [stockError, setStockError] = useState(''); // ← ADD
  const [checkingOut, setCheckingOut] = useState(false); // ← ADD
  
  
  const [selectedIds, setSelectedIds] = useState(new Set());
  const hasInitialized                = useRef(false);



  /* ── Fetch cart on mount ── */
  useEffect(() => {
  if (!getToken()) { navigate('/account'); return; }
  getCart()
    .then(data => {
      console.log('cart loaded:', data);                          // debug
      console.log('cartItemIds:', data.map(i => i.cartItemId));  // debug
      setItems(data);
    })
    .catch(err => console.error('Cart fetch failed:', err))
    .finally(() => setCartLoading(false));
}, [navigate]);

  /* ── Update qty ── */
 const updateQty = async (cartItemId, delta) => {
  const item = items.find(i => i.cartItemId === cartItemId);
  if (!item) return;

  const newQty = Math.max(1, item.qty + delta);
  
  if (item.maxQty && newQty > item.maxQty) {
  // No error toast needed anymore — button is disabled
  // But keep as safety net with clearer message:
  setStockError(`Maximum stock reached for "${item.name}" (${item.maxQty} available)`);
  return;
}

  // ✅ Clear error regardless of whether qty changed
  setStockError('');
  
  if (newQty === item.qty) return; // ← moved AFTER error clear

  setItems(prev => prev.map(i =>
    i.cartItemId === cartItemId ? { ...i, qty: newQty } : i
  ));

  try {
    await updateCartQty(cartItemId, newQty);
  } catch (err) {
    setStockError(err.message);
    setItems(prev => prev.map(i =>
      i.cartItemId === cartItemId ? { ...i, qty: item.qty } : i
    ));
  }
};
 
  const removeItem = async (cartItemId) => {
    // Optimistic update
    setItems(prev => prev.filter(i => i.cartItemId !== cartItemId));
    
    try {
      await removeFromCart(cartItemId);
      window.dispatchEvent(new Event('cart-updated'));
    } catch (err) {
      console.error('Remove failed:', err);
      // Refetch to recover correct state
      getCart().then(setItems);
    }
  };

  /* ── Clear all ── */
  const handleClearAll = async () => {
    setItems([]);
    
    try {
      await clearCart();
      window.dispatchEvent(new Event('cart-updated'));
    } catch (err) {
      console.error('Clear cart failed:', err);
      getCart().then(setItems);
    }
  };

  
   useEffect(() => {
    if (items.length > 0 && !hasInitialized.current) {
      setSelectedIds(new Set(items.map(i => i.cartItemId)));
      hasInitialized.current = true;
    }
  }, [items]);

  const toggleSelect = (cartItemId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(cartItemId) ? next.delete(cartItemId) : next.add(cartItemId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map(i => i.cartItemId)));
  };

  

  


  /* ── Price calculations ── */
  const subtotal    = items.reduce((s, i) => s + i.price * i.qty, 0);
  const savings     = items.reduce((s, i) => s + ((i.mrp ? (i.mrp - i.price) : 0) * i.qty), 0);
  const total       = subtotal;

  const isEmpty     = items.length === 0;
  const totalQty    = items.reduce((s, i) => s + i.qty, 0);
  const shippingPct = Math.min((subtotal / 1499) * 100, 100);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .cart-page {
          background: #FAF8F4;
          min-height: 100vh;
          padding-top: 162px;
          font-family: 'Jost', sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .cart-page::before {
          content: '';
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
        }

        /* ══ Page header ══ */
        .cart-header {
          position: relative;
          z-index: 1;
          padding: 28px clamp(20px, 5vw, 64px) 0;
          max-width: 1200px;
          margin: 0 auto;
        }
        .cart-back {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #8A7E72;
          text-decoration: none;
          transition: color 0.2s;
          margin-bottom: 20px;
        }
        .cart-back:hover { color: #B08D57; }
        .cart-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #1C1A18;
          letter-spacing: -0.02em;
          line-height: 1.05;
          display: flex;
          align-items: baseline;
          gap: 14px;
          flex-wrap: wrap;
        }
        .cart-title em { font-style: italic; font-weight: 400; color: #B08D57; }
        .cart-item-count {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 400;
          color: #8A7E72;
          letter-spacing: 0.06em;
        }

        /* Free shipping progress */
        .cart-shipping-bar {
          margin-top: 20px;
          background: #fff;
          border: 1px solid rgba(176,141,87,0.15);
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .cart-shipping-bar-text {
          font-size: 11px;
          font-weight: 400;
          color: #5C5247;
          letter-spacing: 0.04em;
        }
        .cart-shipping-bar-text strong { color: #B08D57; font-weight: 600; }
        .cart-shipping-track {
          height: 3px;
          background: rgba(176,141,87,0.15);
          border-radius: 2px;
          overflow: hidden;
        }
        .cart-shipping-fill {
          height: 100%;
          background: linear-gradient(to right, #B08D57, #C9A96E);
          border-radius: 2px;
          transition: width 0.5s ease;
        }

        /* ══ Main layout ══ */
        .cart-layout {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px clamp(20px, 5vw, 64px) 80px;
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 24px;
          align-items: start;
        }

        /* ══ Cart items panel ══ */
        .cart-items-panel {
          display: flex;
          flex-direction: column;
          gap: 0;
          background: #fff;
          border: 1px solid rgba(176,141,87,0.12);
        }

        .cart-items-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(176,141,87,0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .cart-items-header-title {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cart-items-header-title::before {
          content: '';
          width: 16px;
          height: 1px;
          background: #B08D57;
        }
        .cart-clear {
          background: none;
          border: none;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #C0392B;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .cart-clear:hover { opacity: 0.7; }

        /* ── Loading state ── */
        .cart-loading-state {
          padding: 60px 20px;
          text-align: center;
          font-size: 12px;
          color: #9A9186;
          letter-spacing: 0.12em;
          font-weight: 300;
        }
        .cart-loading-dots {
          display: inline-flex;
          gap: 5px;
          margin-top: 12px;
        }
        .cart-loading-dots span {
          width: 5px;
          height: 5px;
          background: #B08D57;
          border-radius: 50%;
          animation: dotPulse 1.2s infinite ease-in-out;
        }
        .cart-loading-dots span:nth-child(2) { animation-delay: 0.2s; }
        .cart-loading-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }

        /* ── Item row ── */
        .cart-item {
          display: grid;
          grid-template-columns: 24px 88px 1fr auto;
          gap: 16px;
          padding: 18px 20px;
          border-bottom: 1px solid rgba(176,141,87,0.08);
          align-items: start;
        }
        .cart-item:last-child { border-bottom: none; }

        .cart-item-img {
          width: 88px;
          height: 88px;
          overflow: hidden;
          flex-shrink: 0;
          background: #F0EAE0;
          border-radius: 1px;
          border: 1px solid rgba(176,141,87,0.1);
          position: relative;
        }
        .cart-item-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .cart-item-badge {
          position: absolute;
          top: 5px;
          left: 5px;
          font-size: 7px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 2px 6px;
          border-radius: 1px;
        }

        .cart-item-details { min-width: 0; }
        .cart-item-name {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: #1C1A18;
          text-decoration: none;
          letter-spacing: 0.01em;
          display: block;
          margin-bottom: 5px;
          line-height: 1.35;
          transition: color 0.2s;
        }
        .cart-item-name:hover { color: #B08D57; }
        .cart-item-price-row {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 12px;
        }
        .cart-item-price {
          font-family: 'Playfair Display', serif;
          font-size: 15px;
          font-weight: 700;
          color: #B08D57;
        }
        .cart-item-mrp {
          font-size: 11px;
          color: #AEA39A;
          text-decoration: line-through;
          font-weight: 300;
        }

        .cart-qty-ctrl {
          display: inline-flex;
          align-items: center;
          border: 1px solid rgba(176,141,87,0.25);
        }
        .cart-qty-btn {
          width: 28px;
          height: 28px;
          background: none;
          border: none;
          cursor: pointer;
          color: #5C5247;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s, color 0.2s;
        }
        .cart-qty-btn:hover { background: #B08D57; color: #FAF8F4; }
        .cart-qty-val {
          width: 32px;
          text-align: center;
          font-family: 'Playfair Display', serif;
          font-size: 13px;
          font-weight: 600;
          color: #1C1A18;
          border-left: 1px solid rgba(176,141,87,0.2);
          border-right: 1px solid rgba(176,141,87,0.2);
          padding: 5px 0;
        }

        .cart-item-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
          flex-shrink: 0;
        }
        .cart-item-subtotal {
          font-family: 'Playfair Display', serif;
          font-size: 15px;
          font-weight: 700;
          color: #1C1A18;
          white-space: nowrap;
        }
        .cart-remove {
          background: none;
          border: none;
          cursor: pointer;
          color: #AEA39A;
          transition: color 0.2s;
          padding: 4px;
        }
        .cart-remove:hover { color: #C0392B; }

        /* ══ Empty state ══ */
        .cart-empty {
          padding: 60px 20px;
          text-align: center;
        }
        .cart-empty-icon {
          color: rgba(176,141,87,0.3);
          margin: 0 auto 18px;
          display: block;
        }
        .cart-empty-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 700;
          color: #1C1A18;
          margin-bottom: 8px;
        }
        .cart-empty-sub {
          font-size: 13px;
          font-weight: 300;
          color: #8A7E72;
          margin-bottom: 24px;
          letter-spacing: 0.03em;
        }
        .cart-empty-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #1C1A18;
          color: #FAF8F4;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding: 13px 28px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .cart-empty-btn:hover { background: #B08D57; }

        /* ══ Summary panel ══ */
        .cart-summary {
          background: #fff;
          border: 1px solid rgba(176,141,87,0.12);
          position: sticky;
          top: 180px;
        }

        .cart-summary-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(176,141,87,0.1);
        }
        .cart-summary-title {
          font-family: 'Playfair Display', serif;
          font-size: 17px;
          font-weight: 700;
          color: #1C1A18;
          letter-spacing: -0.01em;
        }
        .cart-summary-title em { font-style: italic; font-weight: 400; color: #B08D57; }

        .cart-summary-body { padding: 18px 20px; }

        /* Price rows */
        .cart-price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          font-size: 12px;
          color: #5C5247;
          letter-spacing: 0.03em;
        }
        .cart-price-row--savings { color: #2E7D32; }
        .cart-price-row--total {
          border-top: 1px solid rgba(176,141,87,0.15);
          padding-top: 12px;
          margin-top: 4px;
        }
        .cart-price-row--total span:first-child {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #1C1A18;
        }
        .cart-price-row--total span:last-child {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #B08D57;
        }

        

        /* Checkout btn */
        .cart-checkout {
          width: 100%;
          background: #1C1A18;
          color: #FAF8F4;
          border: none;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          padding: 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          position: relative;
          overflow: hidden;
          transition: color 0.28s;
          margin-bottom: 8px;
        }
        .cart-checkout::after {
          content: '';
          position: absolute;
          inset: 0;
          background: #B08D57;
          transform: translateX(-101%);
          transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 0;
        }
        .cart-checkout:hover::after { transform: translateX(0); }
        .cart-checkout > * { position: relative; z-index: 1; }
        .cart-checkout:disabled { opacity: 0.5; cursor: not-allowed; }
        .cart-checkout:disabled::after { display: none; }

        .cart-continue {
          width: 100%;
          background: transparent;
          color: #5C5247;
          border: 1px solid rgba(176,141,87,0.25);
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          padding: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          text-decoration: none;
          transition: border-color 0.2s, color 0.2s;
        }
        .cart-continue:hover { border-color: #B08D57; color: #B08D57; }

        /* Trust row */
        .cart-trust {
          display: flex;
          justify-content: space-between;
          padding: 14px 20px;
          border-top: 1px solid rgba(176,141,87,0.1);
          gap: 8px;
        }
        .cart-trust-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          text-align: center;
          flex: 1;
        }
        .cart-trust-icon { color: #B08D57; }
        .cart-trust-label {
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #8A7E72;
        }

        /* ══ Suggested ══ */
        .cart-suggested {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 clamp(20px, 5vw, 64px) 80px;
        }
        .cart-sug-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(20px, 2.5vw, 26px);
          font-weight: 700;
          color: #1C1A18;
          letter-spacing: -0.01em;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .cart-sug-title em { font-style: italic; font-weight: 400; color: #B08D57; }
        .cart-sug-title::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, rgba(176,141,87,0.25), transparent);
        }
        .cart-sug-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .cart-sug-card {
          background: #fff;
          border: 1px solid rgba(176,141,87,0.1);
          border-radius: 2px;
          overflow: hidden;
          cursor: pointer;
          transition: box-shadow 0.3s, border-color 0.3s;
        }
        .cart-sug-card:hover {
          box-shadow: 0 8px 28px rgba(28,26,24,0.08);
          border-color: rgba(176,141,87,0.28);
        }
        .cart-sug-img {
          aspect-ratio: 1/1;
          overflow: hidden;
          background: #F0EAE0;
        }
        .cart-sug-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s;
        }
        .cart-sug-card:hover .cart-sug-img img { transform: scale(1.05); }
        .cart-sug-info { padding: 12px 13px; }
        .cart-sug-name {
          font-size: 12px;
          font-weight: 500;
          color: #1C1A18;
          margin-bottom: 6px;
          line-height: 1.35;
        }
        .cart-sug-price-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
        }
        .cart-sug-price {
          font-family: 'Playfair Display', serif;
          font-size: 14px;
          font-weight: 700;
          color: #B08D57;
        }
        .cart-sug-mrp {
          font-size: 11px;
          color: #AEA39A;
          text-decoration: line-through;
          font-weight: 300;
        }
        .cart-sug-add {
          width: 100%;
          background: #1C1A18;
          color: #FAF8F4;
          border: none;
          font-family: 'Jost', sans-serif;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 9px;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .cart-sug-add:hover { background: #B08D57; }
        .cart-sug-add:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Responsive */
        @media (max-width: 900px) {
          .cart-layout { grid-template-columns: 1fr; }
          .cart-summary { position: static; }
          .cart-sug-grid { grid-template-columns: repeat(2, 1fr); }
          .cart-page { padding-top: 148px; }
        }
        @media (max-width: 480px) {
          .cart-page { padding-top: 136px; }
          .cart-item { grid-template-columns: 24px 72px 1fr; }
          .cart-item-right {
            grid-column: 2;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
          .cart-sug-grid { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>

      <div className="cart-page">

        {/* ── Header ── */}
        <div className="cart-header">
          <Link to="/collections" className="cart-back">
            <ArrowLeft size={12} /> Continue Shopping
          </Link>
          <h1 className="cart-title">
            Your <em>Cart</em>
            {!cartLoading && (
              <span className="cart-item-count">
                {totalQty} item{totalQty !== 1 ? 's' : ''}
              </span>
            )}
          </h1>

          {/* Free shipping progress */}
          {!cartLoading && !isEmpty && (
            <div className="cart-shipping-bar">
             {subtotal >= 1499
            ? <p className="cart-shipping-bar-text">You qualify for <strong>Free base shipping!</strong></p>
            : <p className="cart-shipping-bar-text">Add <strong>{formatPrice(1499 - subtotal)}</strong> more for free base shipping</p>
          }
              <div className="cart-shipping-track">
                <div className="cart-shipping-fill" style={{ width: `${shippingPct}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Main layout ── */}
        <div className="cart-layout">

          {/* Items panel */}
          <div className="cart-items-panel">
             <div className="cart-items-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  checked={!cartLoading && items.length > 0 && selectedIds.size === items.length}
                  onChange={toggleAll}
                  style={{ accentColor: '#B08D57', width: 15, height: 15, cursor: 'pointer' }}
                />
                <p className="cart-items-header-title">Your Items</p>
              </div>
              {!cartLoading && !isEmpty && (
                <button className="cart-clear" onClick={handleClearAll}>Clear All</button>
              )}
            </div>

            {/* Loading state */}
            {cartLoading ? (
              <div className="cart-loading-state">
                Loading your cart
                <div className="cart-loading-dots">
                  <span /><span /><span />
                </div>
              </div>

            /* Empty state */
            ) : isEmpty ? (
              <div className="cart-empty">
                <ShoppingBag size={52} className="cart-empty-icon" />
                <h2 className="cart-empty-title">Your cart is empty</h2>
                <p className="cart-empty-sub">Looks like you haven't added anything yet.</p>
                <Link to="/collections" className="cart-empty-btn">
                  <ShoppingBag size={13} /> Shop Collections
                </Link>
              </div>

            /* Items list */
            ) : (
              <AnimatePresence>
                {items.map(item => (
                  <motion.div
                    key={item.cartItemId}
                    className="cart-item"
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 16, height: 0, padding: 0, overflow: 'hidden' }}
                    transition={{ duration: 0.3 }}
                    layout
                  >
                    
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.cartItemId)}
                      onChange={() => toggleSelect(item.cartItemId)}
                      style={{
                        accentColor: '#B08D57', width: 16, height: 16,
                        alignSelf: 'center', cursor: 'pointer', flexShrink: 0, margin: 0,
                      }}
                    />

                    {/* Image */}
                    <div className="cart-item-img">
                      <img src={item.image || item.images?.[0] || null} alt={item.name} />
                      {item.badge && BADGE_COLORS[item.badge] && (
                        <span
                          className="cart-item-badge"
                          style={{
                            background: BADGE_COLORS[item.badge].bg,
                            color: BADGE_COLORS[item.badge].color,
                          }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </div>

                    {/* Details */}
                    <div className="cart-item-details">
                      <Link to={`/products/${item.id}`} className="cart-item-name">
                          {item.name}
                        </Link>

                        {/* Show selected color/size */}
                        {(item.color || item.size) && (
                          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                            {item.color && (
                              <span style={{
                                fontSize: 9, fontWeight: 600, letterSpacing: '0.14em',
                                textTransform: 'uppercase', color: '#8A7E72',
                                background: '#F5F0E8', padding: '3px 8px',
                              }}>
                                {item.color}
                              </span>
                            )}
                            {item.size && (
                              <span style={{
                                fontSize: 9, fontWeight: 600, letterSpacing: '0.14em',
                                textTransform: 'uppercase', color: '#8A7E72',
                                background: '#F5F0E8', padding: '3px 8px',
                              }}>
                                {item.size}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="cart-item-price-row">
                        <span className="cart-item-price">{formatPrice(item.price)}</span>
                        {item.mrp && (
                          <span className="cart-item-mrp">{formatPrice(item.mrp)}</span>
                        )}
                      </div>
                      {/* NEW — + button disabled at maxQty */}
                      <div className="cart-qty-ctrl">
                        <button
                          className="cart-qty-btn"
                          onClick={() => updateQty(item.cartItemId, -1)}
                          disabled={item.qty <= 1}
                          style={{ opacity: item.qty <= 1 ? 0.35 : 1, cursor: item.qty <= 1 ? 'not-allowed' : 'pointer' }}
                        >
                          <Minus size={11} />
                        </button>
                        <span className="cart-qty-val">{item.qty}</span>
                        <button
                          className="cart-qty-btn"
                          onClick={() => updateQty(item.cartItemId, 1)}
                          disabled={item.maxQty && item.qty >= item.maxQty}
                          style={{ opacity: (item.maxQty && item.qty >= item.maxQty) ? 0.35 : 1, cursor: (item.maxQty && item.qty >= item.maxQty) ? 'not-allowed' : 'pointer' }}
                        >
                          <Plus size={11} />
                        </button>
                      </div>

                      {/* Show max stock hint inline — only when AT max */}
                      {item.maxQty && item.qty >= item.maxQty && (
                        <p style={{ fontSize: 10, color: '#8A7E72', marginTop: 4, letterSpacing: '0.03em' }}>
                          Max available: {item.maxQty}
                        </p>
                      )}
                      {/* WITH this */}
                      {item._originalQty > item.maxQty && (
                        <p style={{ fontSize: 10, color: '#C0392B', marginTop: 4 }}>
                          Qty reduced — only {item.maxQty} left in stock
                        </p>
                      )}
                      </div>

                    {/* Right */}
                    <div className="cart-item-right">
                      <span className="cart-item-subtotal">
                        {formatPrice(item.price * item.qty)}
                      </span>
                      <button className="cart-remove" onClick={() => removeItem(item.cartItemId)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>

          {/* ── Summary panel ── */}
          <div className="cart-summary">
            <div className="cart-summary-header">
              <h2 className="cart-summary-title">Order <em>Summary</em></h2>
            </div>
            <div className="cart-summary-body">

              

              {/* Price breakdown */}
              <div className="cart-price-row">
                <span>Subtotal ({totalQty} item{totalQty !== 1 ? 's' : ''})</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {savings > 0 && (
                <div className="cart-price-row cart-price-row--savings">
                  <span>You Save</span>
                  <span>−{formatPrice(savings)}</span>
                </div>
              )}
              <div className="cart-price-row">
                <span>Shipping</span>
                <span style={{ color: '#8A7E72', fontSize: 11 }}>Calculated at checkout</span>
              </div>
              <div className="cart-price-row cart-price-row--total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>

                          
            {stockError && (
              <div style={{
                background: 'rgba(192,57,43,0.08)',
                border: '1px solid rgba(192,57,43,0.25)',
                padding: '10px 12px',
                marginBottom: 10,
                fontSize: 11,
                color: '#C0392B',
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
              }}>
                {stockError}
              </div>
            )}

            {/* Update button span text */}
            

              {/* Checkout */}
              <button
                className="cart-checkout"
                disabled={isEmpty || cartLoading || selectedIds.size === 0 || !!stockError}
                onClick={async () => {
                    if (checkingOut) return;
                    setStockError('');
                    setCheckingOut(true);

                    const selectedItems = items
                      .filter(i => selectedIds.has(i.cartItemId))
                      .map(i => ({
                        product_id: i.id,
                        id:    i.id,
                        name:  i.name,
                        price: i.price,
                        image: i.image || i.images?.[0] || null,
                        qty:   i.qty,
                        color: i.color || null,
                        size:  i.size  || null,
                      }));

                    try {
                      const res = await fetch(`${API}/products/check-stock`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: selectedItems }),
                      });
                      const data = await res.json();

                      if (data.issues && data.issues.length > 0) {
                        const msg = data.issues.map(i => `• ${i.name}: ${i.issue}`).join('\n');
                        setStockError(`Some items have stock issues:\n${msg}`);
                        // Refresh cart to reflect real stock
                        getCart().then(setItems);
                        setCheckingOut(false);
                        return;
                      }
                    } catch (err) {
                      console.error('Stock check failed:', err);
                      // Don't block — backend will catch it
                    }

                    setCheckingOut(false);
                    navigate('/checkout', { state: { items: selectedItems } });
                  }}
              >
                <ShoppingBag size={13} />
                <span>
                  {cartLoading ? 'Loading…'
                    : checkingOut ? 'Checking stock…'
                    : selectedIds.size === 0 ? 'Select items to checkout'
                    : `Checkout ${selectedIds.size} item${selectedIds.size > 1 ? 's' : ''}`}
                </span>
                
                <ChevronRight size={13} />
              </button>
              <Link to="/collections" className="cart-continue">
                <ArrowLeft size={11} /> Continue Shopping
              </Link>
            </div>

            {/* Trust badges */}
            <div className="cart-trust">
              {[
               { icon: Shield, label: 'Secure Pay' },
               { icon: Truck,  label: 'Fast Ship'  },
              ].map(t => (
                <div key={t.label} className="cart-trust-item">
                  <t.icon size={16} className="cart-trust-icon" />
                  <span className="cart-trust-label">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── You May Also Like ── */}
        
        

      </div>
    </>
  );
};

export default CartPage;